export type LosStatus = 'libera' | 'marginale' | 'ostruita';
export type LosInput = { distanceM: number; ground: number[]; hHomeM: number; hSiteM: number; freqGHz: number };
export type LosResult = { status: LosStatus; worstClearanceM: number; worstIndex: number; lineM: number[]; fresnelM: number[] };

const K = 4 / 3;
const R = 6371000;

/**
 * Radio line of sight between home (ground[0]) and site (ground[last]).
 * Clearance at each point = line height − (terrain + earth bulge). Endpoints are excluded.
 * ostruita: clearance ≤ 0 somewhere; marginale: clearance < 0.6·F1 somewhere; else libera.
 */
export function lineOfSight(i: LosInput): LosResult {
  const n = i.ground.length;
  const h0 = i.ground[0] + i.hHomeM;
  const h1 = i.ground[n - 1] + i.hSiteM;
  const D = i.distanceM;
  const lineM: number[] = [];
  const fresnelM: number[] = [];
  let worstRatio = Infinity;
  let worstIndex = -1;
  let worstClearanceM = Infinity;
  let blocked = false;
  for (let k = 0; k < n; k++) {
    const t = n === 1 ? 0 : k / (n - 1);
    const d1 = D * t;
    const d2 = D - d1;
    const line = h0 + (h1 - h0) * t;
    const f1 = D > 0 ? 17.32 * Math.sqrt(((d1 / 1000) * (d2 / 1000)) / (i.freqGHz * (D / 1000))) : 0;
    lineM.push(line);
    fresnelM.push(f1);
    if (k === 0 || k === n - 1) continue;
    const clearance = line - (i.ground[k] + (d1 * d2) / (2 * K * R));
    // rank points by clearance relative to the 0.6·F1 requirement
    const ratio = f1 > 0 ? clearance / (0.6 * f1) : clearance;
    if (clearance <= 0) blocked = true;
    if (ratio < worstRatio) { worstRatio = ratio; worstIndex = k; worstClearanceM = clearance; }
  }
  const status: LosStatus = blocked ? 'ostruita' : worstRatio < 1 ? 'marginale' : 'libera';
  return { status, worstClearanceM: worstIndex < 0 ? 0 : worstClearanceM, worstIndex, lineM, fresnelM };
}

export const DEFAULT_HOME_HEIGHT_M = 8;
export const DEFAULT_SITE_HEIGHT_M = 30;
export const DEFAULT_FREQ_GHZ = 3.6;
